(() => {
  if (customElements.get('iceyard-reviews')) return;
  class IceyardReviews extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.window = this.querySelector('[data-reviews-window]');
      this.track = this.querySelector('[data-reviews-track]');
      this.original = this.querySelector('[data-reviews-original]');
      if (!this.window || !this.original || !this.original.children.length) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.manual = this.reduced.matches;
      this.hovered = false;
      this.visible = false;
      this.window.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.syncPlayback();
      }, options);
      this.window.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.syncPlayback();
      }, options);
      this.window.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch' || event.pointerType === 'pen') this.setManual(true);
      }, options);
      this.window.addEventListener('focusin', () => this.setManual(true), options);
      this.window.addEventListener('focusout', (event) => {
        if (!this.window.contains(event.relatedTarget)) this.setManual(false);
      }, options);
      ['pointerup', 'pointercancel'].forEach((name) => this.window.addEventListener(name, () => {
        if (!this.window.contains(document.activeElement)) this.setManual(false);
      }, options));
      this.window.addEventListener('keydown', (event) => {
        if (event.target !== this.window || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.setManual(true);
        this.window.scrollBy({ left: (event.key === 'ArrowRight' ? 1 : -1) * this.original.children[0].getBoundingClientRect().width, behavior: this.reduced.matches ? 'instant' : 'smooth' });
      }, options);
      this.reduced.addEventListener('change', () => {
        this.manual = this.reduced.matches;
        this.build();
      }, options);
      document.addEventListener('visibilitychange', () => this.syncPlayback(), options);
      this.resizeObserver = new ResizeObserver(() => {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.build());
      });
      this.resizeObserver.observe(this.window);
      this.resizeObserver.observe(this.original);
      this.visibilityObserver = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        this.syncPlayback();
      });
      this.visibilityObserver.observe(this);
      this.build();
      document.fonts?.ready.then(() => { if (this.isConnected) this.build(); });
    }

    position() {
      if (!this.animation || !this.duration) return this.window.scrollLeft;
      return ((Number(this.animation.currentTime) || 0) % this.duration) / this.duration * this.distance;
    }

    setManual(manual) {
      if (this.reduced.matches) manual = true;
      if (this.manual === manual) return;
      const position = this.position();
      this.manual = manual;
      this.build(position);
    }

    build(position = this.position()) {
      this.animation?.cancel();
      this.animation = null;
      this.track.querySelectorAll('[data-reviews-clone]').forEach((clone) => clone.remove());
      this.distance = this.original.getBoundingClientRect().width;
      const viewport = this.window.clientWidth;
      this.dataset.running = String(!this.manual);
      if (this.manual || !this.distance || !viewport) {
        this.window.scrollLeft = position;
        return;
      }
      const copies = Math.ceil(viewport / this.distance) + 1;
      for (let index = 0; index < copies; index++) {
        const clone = this.original.cloneNode(true);
        clone.removeAttribute('data-reviews-original');
        clone.setAttribute('data-reviews-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        clone.querySelectorAll('[data-shopify-editor-block]').forEach((node) => node.removeAttribute('data-shopify-editor-block'));
        clone.querySelectorAll('a,button,[tabindex]').forEach((node) => node.setAttribute('tabindex', '-1'));
        this.track.appendChild(clone);
      }
      this.window.scrollLeft = 0;
      this.duration = this.distance / Math.max(15, Math.min(60, Number(this.dataset.speed) || 30)) * 1000;
      this.animation = this.track.animate([
        { transform: 'translateX(0)' },
        { transform: `translateX(${-this.distance}px)` }
      ], { duration: this.duration, iterations: Infinity, easing: 'linear' });
      this.animation.currentTime = ((position % this.distance) / this.distance) * this.duration;
      this.syncPlayback();
    }

    syncPlayback() {
      if (!this.animation) return;
      if (this.hovered || document.hidden || !this.visible) this.animation.pause();
      else this.animation.play();
    }

    disconnectedCallback() {
      this.animation?.cancel();
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      cancelAnimationFrame(this.resizeFrame);
      this.controller?.abort();
      this.controller = null;
    }
  }
  customElements.define('iceyard-reviews', IceyardReviews);
})();

